/**
 * EnterpriseSetupModal.jsx
 * ========================
 * 
 * Modal de création d'entreprise.
 * 
 * RÈGLES:
 * - Appel backend POST /api/company uniquement
 * - Aucune logique métier
 */

import React, { useState } from 'react';
import { X, Building2, Mail, FileText, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { safeFetchJSON } from '../../utils/apiHelpers';
import { getBackendUrl } from '../../config/environment';

const BACKEND_URL = getBackendUrl();

const EnterpriseSetupModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    billing_email: '',
    billing_address: '',
    vat_number: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    if (!formData.billing_email.trim()) {
      newErrors.billing_email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billing_email)) {
      newErrors.billing_email = 'Email invalide';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const data = await safeFetchJSON(`${BACKEND_URL}/api/company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          billing_email: formData.billing_email.trim(),
          billing_address: formData.billing_address.trim() || null,
          vat_number: formData.vat_number.trim() || null
        })
      });
      
      // 🔒 Option D MOA: Gérer le cas où l'entreprise existante est retournée
      if (data.message && data.message.includes('déjà administrateur')) {
        toast.success('Entreprise existante sélectionnée');
      } else {
        toast.success('Entreprise créée avec succès');
      }
      
      onSuccess(data);
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error(error.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {/* 🔒 P1 MOA (Jan 2026): Fix mobile - items-start + overflow-y-auto pour garantir accessibilité */}
      <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal - max-h-[90vh] pour ne jamais dépasser l'écran */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md max-h-[90vh] bg-white rounded-2xl shadow-2xl border border-[#DDD9D0] overflow-hidden my-4 flex flex-col"
        >
          {/* Header - sticky pour toujours être visible */}
          <div className="flex items-center justify-between p-6 border-b border-[#E8E5DE] flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#92710A]" />
              </div>
              <h2 className="text-xl font-bold text-[#111827]">Créer une entreprise</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-[#6B7280]" />
            </button>
          </div>
          
          {/* Form - scrollable si contenu dépasse */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-[#4B5563] mb-2">
                Nom de l&apos;entreprise *
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Ex: Acme Corporation"
                  className={`w-full bg-white/5 border ${errors.name ? 'border-red-500' : 'border-[#DDD9D0]'} rounded-xl pl-11 pr-4 py-3 text-[#111827] placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]`}
                />
              </div>
              {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
            </div>
            
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-[#4B5563] mb-2">
                Email de facturation *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                <input
                  type="email"
                  name="billing_email"
                  value={formData.billing_email}
                  onChange={handleChange}
                  placeholder="facturation@entreprise.com"
                  className={`w-full bg-white/5 border ${errors.billing_email ? 'border-red-500' : 'border-[#DDD9D0]'} rounded-xl pl-11 pr-4 py-3 text-[#111827] placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]`}
                />
              </div>
              {errors.billing_email && <p className="text-red-400 text-sm mt-1">{errors.billing_email}</p>}
            </div>

            {/* Billing Address */}
            <div>
              <label className="block text-sm font-medium text-[#4B5563] mb-2">
                Adresse de facturation
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                <input
                  type="text"
                  name="billing_address"
                  value={formData.billing_address}
                  onChange={handleChange}
                  placeholder="12 rue de la Paix, 75002 Paris"
                  className="w-full bg-white/5 border border-[#DDD9D0] rounded-xl pl-11 pr-4 py-3 text-[#111827] placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                />
              </div>
            </div>
            
            {/* VAT Number (optional) */}
            <div>
              <label className="block text-sm font-medium text-[#4B5563] mb-2">
                Numéro de TVA <span className="text-gray-500">(optionnel)</span>
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                <input
                  type="text"
                  name="vat_number"
                  value={formData.vat_number}
                  onChange={handleChange}
                  placeholder="FR12345678901"
                  className="w-full bg-white/5 border border-[#DDD9D0] rounded-xl pl-11 pr-4 py-3 text-[#111827] placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                />
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 border border-[#DDD9D0] rounded-xl text-[#111827] font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] hover:brightness-110 disabled:opacity-50 rounded-xl text-[#111827] font-medium flex items-center justify-center gap-2 transition-all shadow-[0_2px_8px_rgba(180,150,40,0.25)]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Création...
                  </>
                ) : (
                  'Créer l\'entreprise'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EnterpriseSetupModal;
